import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LayoutDashboard, Inbox, MessageSquare, Clapperboard, UploadCloud, Users, LogOut, ExternalLink, ArrowRight, Film, Lightbulb, LibraryBig, CalendarDays, Briefcase, BookOpen } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";
import { usePerms } from "@/components/PermsContext";
import { PrivateHeader } from "@/components/dashboard/PrivateHeader";
import { AdminProduction } from "@/components/admin/AdminProduction";
import { AdminSubmissions } from "@/components/admin/AdminSubmissions";
import { AdminApplications } from "@/components/admin/AdminApplications";
import { AdminTeam } from "@/components/admin/AdminTeam";
import { AdminMessages } from "@/components/admin/AdminMessages";
import { AdminVideos } from "@/components/admin/AdminVideos";
import { AdminIdeas } from "@/components/admin/AdminIdeas";
import { AdminLibrary } from "@/components/admin/AdminLibrary";
import { ManualsPage } from "@/components/manuals/ManualsPage";
import { AdminCalendar } from "@/components/admin/AdminCalendar";
import { WorkerDashboard } from "@/components/dashboard/WorkerDashboard";
import { ChangePasswordCard } from "@/components/dashboard/ChangePasswordCard";
import { PaymentDetailsForm } from "@/components/dashboard/PaymentDetailsForm";
import { CommandPalette } from "@/components/admin/CommandPalette";
import { TasksPanel } from "@/components/admin/TasksPanel";

export const ManagerHome = ({ me, onNavigate }) => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    Promise.allSettled([
      axios.get(`${API}/admin/jobs`, { headers: authHeaders() }),
      axios.get(`${API}/admin/submissions`, { headers: authHeaders() }),
      axios.get(`${API}/applications`, { headers: authHeaders() }),
    ]).then(([jobs, subs, apps]) => {
      const j = jobs.status === "fulfilled" ? jobs.value.data : [];
      const s = subs.status === "fulfilled" ? subs.value.data : [];
      const a = apps.status === "fulfilled" ? apps.value.data : [];
      const today = new Date().toISOString().slice(0, 10);
      setStats({
        active: j.filter((x) => x.status === "active").length,
        drafts: j.filter((x) => x.status === "draft").length,
        unclaimed: j.filter((x) => x.status === "available").length,
        awaiting: s.filter((x) => x.status === "awaiting_review").length,
        inRevision: s.filter((x) => x.status === "revision_requested").length,
        managerApproved: s.filter((x) => x.status === "manager_approved").length,
        completedToday: s.filter((x) => x.status === "approved" && (x.created_at || "").slice(0, 10) === today).length,
        pendingApps: a.filter((x) => ["pending", "reviewing"].includes(x.status)).length,
      });
    });
  }, []);

  const greeting = () => {
    const h = new Date().getHours();
    return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
  };

  return (
    <div data-testid="manager-home">
      <div className="rounded-3xl border border-[var(--pv-border)] bg-gradient-to-br from-cyan-500 to-cyan-600 p-8 text-[var(--pv-text)] sm:p-10">
        <h1 className="font-display text-2xl font-bold sm:text-3xl" data-testid="manager-greeting">
          {greeting()}, {me.name || "Manager"}
        </h1>
        <p className="mt-2 text-sm text-cyan-50">Here's what production needs from you today.</p>
        {stats && (
          <div data-testid="manager-today-strip" className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-cyan-50">
            <span>Active jobs: <b className="text-white">{stats.active}</b></span>
            <span>Awaiting review: <b className="text-white">{stats.awaiting}</b></span>
            <span>In revision: <b className="text-white">{stats.inRevision}</b></span>
            <span>Completed today: <b className="text-white">{stats.completedToday}</b></span>
            <span>Unclaimed: <b className="text-white">{stats.unclaimed}</b></span>
          </div>
        )}
      </div>

      <div className="mt-6">
        <TasksPanel onNavigate={onNavigate} testid="manager-tasks-panel" title="Needs attention" />
      </div>

      <div data-testid="manager-quick-actions" className="mt-6 flex flex-wrap gap-2">
        {[
          ["Review submissions", "submissions", "mgr-qa-review"],
          ["View production", "production", "mgr-qa-production"],
          ["Message team", "messages", "mgr-qa-messages"],
          ["Review applicants", "applications", "mgr-qa-applicants"],
          ["Read manuals", "manuals", "mgr-qa-manuals"],
          ["Create job draft", "production", "mgr-qa-draft"],
        ].map(([label, section, testid]) => (
          <button key={testid} data-testid={testid} onClick={() => onNavigate(section)}
            className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-4 py-2 text-xs font-semibold text-[var(--pv-text-2)] transition-colors hover:border-cyan-500/30 hover:text-cyan-400">
            {label} <ArrowRight className="h-3 w-3" />
          </button>
        ))}
      </div>

      {!me.demo && (
        <div className="mt-6">
          <ChangePasswordCard testid="manager-change-password" />
        </div>
      )}

      {/* Staff payout details — how the owner pays this staff member's compensation.
          Same secure system workers use; visible only to the staff member + owner. */}
      {!me.demo && (
        <div data-testid="manager-payment-details" className="mt-6 rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6 sm:p-8">
          <h2 className="font-display text-lg font-bold text-[var(--pv-text)]">Payment Details</h2>
          <p className="mt-1 text-xs text-[var(--pv-text-4)]">
            How the owner pays your staff compensation. Only you and the owner can see this — it never changes your compensation amount or schedule.
          </p>
          <div className="mt-5">
            <PaymentDetailsForm />
          </div>
        </div>
      )}
    </div>
  );
};

export const ManagerApp = ({ me, theme, onToggleTheme }) => {
  const [section, setSection] = useState("overview");
  const [msgUnread, setMsgUnread] = useState(0);
  const navigate = useNavigate();
  const { has, loaded } = usePerms();

  const loadUnread = useCallback(() => {
    axios
      .get(`${API}/admin/messages/unread`, { headers: authHeaders() })
      .then((r) => setMsgUnread(r.data.unread_senders))
      .catch((err) => {
        if (err.response?.status === 401) navigate("/login");
      });
  }, [navigate]);

  useEffect(() => {
    loadUnread();
    const t = setInterval(loadUnread, 30000);
    return () => clearInterval(t);
  }, [loadUnread]);

  const NAV = [
    { id: "overview", label: "Overview", icon: LayoutDashboard, show: true },
    { id: "mywork", label: "My Work", icon: Briefcase, show: (me.animator_roles || []).length > 0 },
    { id: "ideas", label: "Ideas", icon: Lightbulb, show: has("view_jobs") },
    { id: "library", label: "Library", icon: LibraryBig, show: has("view_jobs") },
    { id: "manuals", label: "Manuals", icon: BookOpen, show: true },
    { id: "calendar", label: "Calendar", icon: CalendarDays, show: has("view_jobs") },
    { id: "production", label: "Production", icon: Clapperboard, show: has("view_jobs") },
    { id: "submissions", label: "Submissions", icon: UploadCloud, show: has("review_submissions") },
    { id: "videos", label: "Videos", icon: Film, show: has("view_jobs") },
    { id: "applications", label: "Applications", icon: Inbox, show: has("review_applicants") },
    { id: "team", label: "Team", icon: Users, show: has("view_workers") },
    { id: "messages", label: "Messages", icon: MessageSquare, badge: msgUnread, show: has("message_workers") },
  ].filter((n) => n.show);

  const sections = {
    overview: <ManagerHome me={me} onNavigate={setSection} />,
    mywork: <WorkerDashboard me={me} theme={theme} onToggleTheme={onToggleTheme} embedded />,
    ideas: <AdminIdeas canDelete={has("delete_records")} />,
    library: <AdminLibrary canDelete={has("delete_records")} />,
    manuals: <ManualsPage />,
    calendar: <AdminCalendar onNavigate={(sec, testid) => { if (sections[sec]) { setSection(sec); if (testid) setTimeout(() => { const el = document.querySelector(`[data-testid="${testid}"]`); if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("ring-2", "ring-cyan-400"); setTimeout(() => el.classList.remove("ring-2", "ring-cyan-400"), 2600); } }, 450); } }} />,
    production: <AdminProduction />,
    submissions: <AdminSubmissions />,
    videos: <AdminVideos readOnly />,
    applications: <AdminApplications />,
    team: <AdminTeam />,
    messages: <AdminMessages onRead={loadUnread} viewerRole="manager" />,
  };

  return (
    <div data-testid="manager-app" className="min-h-screen bg-[var(--pv-bg)] text-[var(--pv-text)] antialiased">
      <CommandPalette onNavigate={(s) => { if (sections[s]) setSection(s); else if (s === "ideas" || s === "library" || s === "calendar") setSection(s); }} />
      <PrivateHeader title="Melto Studios" badge="Manager" theme={theme} onToggleTheme={onToggleTheme} />
      <div className="flex">
        <aside className="sticky top-16 hidden h-[calc(100vh-4rem)] w-56 shrink-0 flex-col border-r border-[var(--pv-border)] bg-[var(--pv-card)] md:flex">
          <nav className="flex-1 space-y-1 p-4" data-testid="manager-nav">
            {NAV.map((item) => (
              <button
                key={item.id}
                data-testid={`manager-nav-${item.id}`}
                onClick={() => setSection(item.id)}
                className={`flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
                  section === item.id ? "bg-cyan-500 text-white" : "text-[var(--pv-text-3)] hover:bg-[var(--pv-subtle)] hover:text-[var(--pv-text)]"
                }`}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
                {item.badge > 0 && (
                  <span data-testid="manager-messages-badge" className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[10px] font-bold text-white">
                    {item.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
          <div className="space-y-1 border-t border-[var(--pv-border)] p-4">
            <a href="/" target="_blank" rel="noreferrer" className="flex w-full items-center gap-3 rounded-xl px-4 py-2 text-sm font-medium text-[var(--pv-text-4)] hover:bg-[var(--pv-subtle)]">
              <ExternalLink className="h-4 w-4" /> View site
            </a>
            <button
              data-testid="manager-logout-btn"
              onClick={() => { localStorage.removeItem("melto_token"); navigate("/login"); }}
              className="flex w-full items-center gap-3 rounded-xl px-4 py-2 text-sm font-medium text-[var(--pv-text-4)] hover:bg-[var(--pv-subtle)]"
            >
              <LogOut className="h-4 w-4" /> Log out
            </button>
          </div>
        </aside>
        <main className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8">
          <div className="mb-4 flex gap-2 overflow-x-auto md:hidden">
            {NAV.map((item) => (
              <button
                key={item.id}
                data-testid={`manager-nav-mobile-${item.id}`}
                onClick={() => setSection(item.id)}
                className={`flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                  section === item.id ? "bg-cyan-500 text-white" : "border border-[var(--pv-border)] bg-[var(--pv-card)] text-[var(--pv-text-4)]"
                }`}
              >
                <item.icon className="h-3.5 w-3.5" />
                {item.label}
              </button>
            ))}
          </div>
          {sections[section]}
        </main>
      </div>
    </div>
  );
};
